import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { AlertTriangle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface LoginComponentProps {
  onSuccess?: () => void;
}

export function LoginComponent({ onSuccess }: LoginComponentProps) {
  const { login, error: authError, clearError, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Clear errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Update local error state when auth error changes
  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  // Call onSuccess when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && onSuccess) setLoading(false);
  }, [isAuthenticated, onSuccess]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    console.log('[LoginComponent submit] click', { emailMasked: email.replace(/(^.).*(@.*$)/, '$1***$2') });
    
    // Basic validation
    if (!email.trim()) {
      setError('E-post er påkrevd');
      return;
    }
    
    if (!password.trim()) {
      setError('Passord er påkrevd');
      return;
    }
    
    try {
      setLoading(true);
      console.log('[LoginComponent submit] starting login');
      
      // Check if Supabase is connected
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        setError('Supabase er ikke konfigurert. Klikk "Connect to Supabase" for å sette opp tilkoblingen.');
        return;
      }
      
      const res = await login(email, password);
      console.log('[LoginComponent submit] login resolved', { userId: res?.id });
    } catch (err) {
      setLoading(false); // Stop loading on error
      console.log('[LoginComponent submit] login error', err);
      if (err instanceof Error) {
        if (err.message === "Invalid login credentials") {
          setError('Ugyldig e-post eller passord. Sjekk at du har skrevet riktig.');
        } else {
          setError(err.message);
        }
      } else {
        setError('En ukjent feil oppstod');
      }
    }
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            E-post
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            placeholder="din@epost.no"
            disabled={loading}
            autoComplete="email"
            name="email"
          />
        </div>
        
        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Passord
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark pr-10"
              placeholder="••••••••"
              disabled={loading}
              autoComplete="current-password"
              name="password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
        
        {/* Forgot password link */}
        <div className="text-right">
          <Link 
            to="/forgot-password" 
            className="text-sm text-condo-dark hover:text-condo-med hover:underline"
          >
            Glemt passord?
          </Link>
        </div>
        
        {/* Submit button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Logger inn...
            </>
          ) : (
            'Logg inn'
          )}
        </Button>
      </form>
    </div>
  );
}
